// src/App.jsx
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { NotificationProvider } from './components/NotificationSystem';

import Home from './pages/Home';
import Editor from './pages/Editor';
import ModelTest from './pages/ModelTest';
import Auth from './pages/Auth';
import Pricing from './pages/Pricing';
import Features from './pages/Features';
import Support from './pages/Support';
import Dashboard from './pages/Dashboard';
import Blog from './pages/Blog';
import Careers from './pages/Careers';
import Press from './pages/Press';
import Partnerships from './pages/Partnerships';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Policies from './pages/Policies';
import Acknowledgements from './pages/Acknowledgements';
import TestPage from './components/TestPage';
import ImageMapTest from './components/ImageMapTest';
import Login from './pages/Developer';
import ProtectedRoute from './components/ProtectedRoute';
import NotFound404 from './pages/404';
import SEOController from './components/SEOController';
import PaymentSuccess from './pages/PaymentSuccess';
import Access from './pages/Access';
import RenderProAPI from './pages/RenderProAPI';
import WindowLens from './components/WindowLens';

// NEW IMPORTS – adjust these to match your actual component names and paths
import Residential from './pages/Residential';
import Amenity from './pages/ResidentialAmenity';
import Service from './pages/ResidentialService';

// IMPORT THE NEW WELCOME SCREEN
import Welcome from './pages/Welcome';

// Theme Color Manager Component
function ThemeColorManager() {
  const location = useLocation();
  const [cameraOpen, setCameraOpen] = useState(false);
  
  useEffect(() => {
    const handleCameraStateChange = (event) => {
      if (event.detail && event.detail.cameraOpen !== undefined) {
        setCameraOpen(event.detail.cameraOpen);
      }
    };
    
    window.addEventListener('cameraStateChange', handleCameraStateChange);
    
    return () => {
      window.removeEventListener('cameraStateChange', handleCameraStateChange);
    };
  }, []);
  
  useEffect(() => {
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                  (window.navigator.standalone === true);
    
    if (metaThemeColor) {
      if (location.pathname === '/dashboard') {
        if (cameraOpen) {
          metaThemeColor.content = '#333';
          document.documentElement.style.backgroundColor = '#333';
          document.body.style.backgroundColor = '#333';
        } else {
          metaThemeColor.content = '#2D2D2D';
          document.documentElement.style.backgroundColor = '#2D2D2D';
          document.body.style.backgroundColor = '#2D2D2D';
        }
      } else {
        metaThemeColor.content = '#000000';
        // Background handled by index.html inline styles
      }
    }
  }, [location.pathname, cameraOpen]);
  
  return null;
}

function AppContent() {
  return (
    <Router>
      <ThemeColorManager />
      <SEOController />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={(window.location.hostname === 'developer.outlne.com' || window.location.hostname === 'www.developer.outlne.com') ? <RenderProAPI /> : <Home />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/developer" element={<Login />} />
        <Route path="/model-test" element={<ModelTest />} />
        <Route path="/modeltest" element={<ModelTest />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/features" element={<Features />} />
        <Route path="/support" element={<Support />} />
        <Route path="/windowlens" element={<WindowLens />} />
        <Route path="/access" element={<Access />} />
        <Route path="/api" element={<Navigate to="/" replace />} />
        <Route path="/wallet" element={<Navigate to="/access" replace />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/press" element={<Press />} />
        <Route path="/partnerships" element={<Partnerships />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/policycenter" element={<Policies />} />
        <Route path="/acknowledgements" element={<Acknowledgements />} />
        <Route path="/testpage" element={<TestPage />} />

        {/* NEW RESIDENTIAL ROUTES */}
        <Route path="/residential" element={<Residential />} />
        <Route path="/residential/amenity" element={<Amenity />} />
        <Route path="/residential/service" element={<Service />} />

        {/* Protected Routes - Require authentication */}
        <Route 
          path="/payment-success" 
          element={
            <ProtectedRoute>
              <PaymentSuccess />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/editor" 
          element={
            <ProtectedRoute>
              <Editor />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/imagemaptest" 
          element={
            <ProtectedRoute>
              <ImageMapTest />
            </ProtectedRoute>
          } 
        />
        {/* NEW WELCOME ROUTE – shown only to authenticated users */}
        <Route 
          path="/welcome" 
          element={
            <ProtectedRoute>
              <Welcome />
            </ProtectedRoute>
          } 
        />
        
        {/* 404 Page - Must be last */}
        <Route path="*" element={<NotFound404 />} />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <AppContent />
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;